import { Component, inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';

// PrimeNG Modules
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';

import { FooterWidget } from '../footerwidget';
import { SERVICES_DB, ServiceData } from '../../data/service.data';
import { ContactService } from '@/landing/services/contact.service';
import { SeoService } from '@/landing/services/seo.service';
import { DialogModule } from 'primeng/dialog';
import { WhatsappWidget } from '../whatsapp-widget.component';
import { environment } from 'src/environments/environment';


// Define estas Regex fuera de la clase o justo dentro para usarlas
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
const phoneRegex = /^[0-9]{8,15}$/; // Permite +, números, espacios y guiones. Entre 8 y 15 chars.


@Component({
    selector: 'app-service-detail',
    standalone: true,
    providers: [MessageService],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FooterWidget,
        InputTextModule,
        TextareaModule,
        SelectModule,
        ButtonModule,
        DialogModule,
        WhatsappWidget
    ],
    template: `
        <div class="flex flex-column min-h-screen bg-usina-bg">
            

            <main class="flex-grow-1 pt-2 md:pt-4 mb-8">
                @if (service) {
                    <div class="grid grid-nogutter mx-auto">

                        <!-- Header Responsivo -->
                        <div class="col-12 px-0 md:px-8 mb-0 md:mb-4 flex flex-column md:flex-row align-items-stretch md:align-items-baseline">
                            
                            <!-- Versión Mobile (Pleno Negro) -->
                            <div class="flex md:hidden bg-black w-full p-4 align-items-center gap-3">
                                <i class="flex-none pi pi-arrow-left text-3xl cursor-pointer text-white" (click)="navigate('#services')"></i>
                                <span class="header-title underline text-white">Membresias:</span>
                            </div>

                            <!-- Versión Desktop -->
                            <div class="hidden md:flex align-items-baseline gap-3 w-full">
                                <i class="flex-none pi pi-arrow-left text-3xl cursor-pointer hover:text-primary" (click)="navigate('#services')"></i>
                                <div class="flex flex-column md:flex-row md:align-items-baseline md:flex-grow-1 gap-2">
                                    <span class="header-title underline">Membresias: </span>
                                    <span class="header-subtitle">Private spaces. </span>
                                    <span class="header-subtitle">Flexible models. </span>
                                    <span class="header-subtitle">A powerful community.</span>
                                </div>
                                <div class="hidden lg:flex justify-content-end align-items-center flex-shrink-0">
                                    <a [href]="loginUrl" class="user-link cursor-pointer text-center">
                                        <i class="pi pi-user text-xl text-900"></i>
                                        <span class="block">Log in</span>
                                    </a>
                                </div>
                            </div>

                        </div>
                        
                        <div class="col-12 p-0 lg:col-8 md:px-8 mb-6 lg:mb-0">
                            <img [src]="'/assets/img/home/' + service.image" 
                                [alt]="service.title" 
                                class="w-full h-auto block relative z-1 service-image" 
                                style="min-height: 250px; display: block; margin-top: -1px;">
                        </div>

                        <div class="col-12 lg:col-4 px-4 relative">
                            
                            <div class="mb-4 ">
                                <h1 class="font-heavy text-4xl md:text-6xl mb-4 line-height-1">{{ service.title }}</h1>
                                <h2 class="font-medium text-3xl md:text-5xl mb-4 md:line-height-2">
                                    {{ service.subtitle }}
                                </h2>
                                <p class="font-regular text-700 mt-4 md:pr-8" style="color: #3744CF !important">
                                    {{ service.description }}
                                </p>
                            </div>

                            @if (!showForm) {
                                <div class="fade-in">
                                    
                                    <div class="mb-6">
                                        <ul class="list-disc pl-4 m-0">
                                            @for(feature of service.features; track feature){
                                                <li class="font-regular text-lg text-700">{{ feature }}</li>
                                            }
                                        </ul>
                                    </div>

                                    <div class="flex flex-column gap-1 mb-4 pl-2">
                                        @for(price of service.pricing; track price.name){
                                            <div class="flex flex-column md:flex-row md:align-items-baseline gap-2">
                                                <h4 class="font-bold text-2xl">{{ price.name }}</h4>
                                                <h4 class="font-bold text-2xl">{{ price.value }}</h4>
                                            </div>
                                            @if(price.detail) {
                                                <h4 class="text-sm text-600 ml-2">{{ price.detail }}</h4>
                                            }
                                        }
                                    </div>
                                    <button pButton type="button" label="Contactar" 
                                            (click)="toggleForm()"
                                            class="font-bold text-lg border-round-2xl usina-btn-submit px-4 ml-2">
                                    </button>

                                </div>
                            } 
                            
                            @else {
                                <div class="fade-in ">
                                    
                                    <div class="mb-4">
                                        <a (click)="toggleForm()" class="cursor-pointer text-700 hover:text-primary font-medium text-sm flex align-items-center gap-2 transition-colors duration-200">
                                            <i class="pi pi-arrow-left"></i> Volver a detalles
                                        </a>
                                    </div>



                                    <form [formGroup]="form" (ngSubmit)="onSubmit()" >
                                        
                                        <div class="flex flex-column gap-3"> 
                                            <div class="mb-2">
                                                <label class="font-medium text-900 mb-2 block">Seleccioná tu Sede</label>
                                                <p-select formControlName="sede" [options]="sedes" optionLabel="name" optionValue="id" placeholder="Elegí una ubicación" styleClass="w-full usina-dropdown-filled"></p-select>
                                                @if (form.get('sede')?.invalid && form.get('sede')?.touched) {
                                                    <small class="text-red-500 block mt-1">Por favor seleccioná una sede.</small>
                                                }
                                            </div>

                                            <div class="mb-2">
                                                <input pInputText formControlName="name" placeholder="Nombre" class="w-full usina-input" />
                                                @if (form.get('name')?.invalid && form.get('name')?.touched) {
                                                    <small class="text-red-500 block mt-1">El nombre debe tener al menos 3 caracteres.</small>
                                                }
                                            </div>

                                            <div class="mb-2">
                                                <input pInputText formControlName="phone" placeholder="Teléfono" class="w-full usina-input" />
                                                @if (form.get('phone')?.invalid && form.get('phone')?.touched) {
                                                    <small class="text-red-500 block mt-1">Ingresá un teléfono válido   .</small>
                                                }
                                            </div>

                                            <div class="mb-2">
                                                <input pInputText formControlName="email" placeholder="Email" class="w-full usina-input" />
                                                @if (form.get('email')?.invalid && form.get('email')?.touched) {
                                                    <small class="text-red-500 block mt-1">Ingresá un correo electrónico válido.</small>
                                                }
                                            </div>

                                            <div class="mb-2">
                                                <textarea pInputTextarea formControlName="message" placeholder="Mensaje" rows="3" class="w-full usina-input resize-none"></textarea>
                                                @if (form.get('message')?.invalid && form.get('message')?.touched) {
                                                    <small class="text-red-500 block mt-1">El mensaje debe tener al menos 15 caracteres.</small>
                                                }
                                            </div>
                                        </div>
                                        

                                        <button pButton type="submit" label="Enviar" 
                                                [loading]="isSubmitting"
                                                [disabled]="form.invalid || isSubmitting"
                                                class="border-round-2xl font-bold text-lg mt-2 usina-btn-submit px-4">
                                        </button>

                                    </form>
                                </div>
                            }

                        </div>

                    </div>
                }@else {
                    <div class="h-screen flex align-items-center justify-content-center">
                        <p>Cargando información del servicio...</p>
                    </div>
                }
            </main>

            <footer-widget [showForm]="false"></footer-widget>
            
            <p-dialog [(visible)]="showSuccessModal" 
                    [modal]="true" 
                    [closable]="false" 
                    [draggable]="false"
                    [style]="{ width: '90%', maxWidth: '450px' }" 
                    styleClass="border-round-2xl overflow-hidden shadow-4">
                
                <div class="w-full h-1rem" style="background-color: #00ff00;"></div>

                <div class="flex flex-column align-items-center text-center p-5 md:p-6 surface-0">
                    
                    <div class="flex align-items-center justify-content-center border-circle mb-4" 
                        style="width: 5rem; height: 5rem; background-color: rgba(0, 255, 0, 0.1);">
                        <i class="pi pi-check text-primary" style="font-size: 2.5rem;"></i>
                    </div>

                    <h3 class="font-heavy text-4xl text-900 mt-0 mb-3">¡Mensaje enviado!</h3>
                    
                    <p class="font-regular text-lg text-700 mb-5 line-height-3">
                        Gracias por contactarnos. El equipo de Usina revisará tu consulta y te responderá a la brevedad.
                    </p>

                    <button pButton label="ENTENDIDO" 
                            (click)="showSuccessModal = false" 
                            class="w-full p-button-rounded font-bold text-lg usina-btn-submit">
                    </button>

                </div>
            </p-dialog>

            <whatsapp-widget></whatsapp-widget>

        </div>
    `,
    styles: [`
        .bg-usina-bg { background-color: var(--usina-bg, #F2F2F2); }
        .text-primary { color: #00ff00 !important; }

        .header-title {
            font-family: "ABCDiatype-Medium", sans-serif;
            font-size: 2.8rem;
            color: #000000;  
        }

        .header-subtitle {
            font-family: "ABCDiatype-Regular", sans-serif;
            font-size: 1.4rem;
            color: #000000;  
        }

        /* --- CLASE RESPONSIVE PARA LA IMAGEN FLOTANTE LDI --- */
        .ldi-image {
            bottom: -6rem; 
            left: -1rem; 
            width: 50rem;
        }

        /* En Mobile y Tablets chicas limitamos su tamaño para que no rompa el layout */
        @media (max-width: 991px) {
            .ldi-image {
                bottom: -2rem; /* Sube un poco para no tapar tanto texto abajo */
                left: 0rem;
                width: 85%; /* En base al contenedor, nunca más grande que la pantalla */
                max-width: 24rem; /* Límite para que no se vea desproporcionada */
            }
        }
        
        /* Imagen del servicio - Borde inferior neón en mobile */
        @media (max-width: 768px) {
            .service-image {
                border-bottom: 4px solid #00ff00;
            }
        }
        
        /* Animaciones */
        .fade-in {
            animation: fadeIn 0.4s ease-out forwards;
        }
        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
        }

        /* Animación suave para la firma flotante */
        .slide-up {
            animation: slideUp 0.8s ease-out forwards;
        }
        @keyframes slideUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 0.9; transform: translateY(0); }
        }

        .font-heavy { font-family: "ABCDiatype-Heavy", sans-serif; }
        .font-medium { font-family: "ABCDiatype-Medium", sans-serif; }
        .font-regular { font-family: "ABCDiatype-Regular", sans-serif; }

        /* Inputs */
        :host ::ng-deep .usina-input {
            background: transparent;
            border: none;
            border-bottom: 1px solid #ccc;
            border-radius: 0;
            padding: 10px 0;
            font-family: "ABCDiatype-Regular", sans-serif;
            color: #000;
        }
        :host ::ng-deep .usina-input:focus {
            border-bottom-color: #00ff00;
            box-shadow: none;
        }

        /* Dropdown */
        :host ::ng-deep .usina-dropdown-filled {
            background: #F8F9FA;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
        }
        :host ::ng-deep .usina-dropdown-filled.p-focus {
            border-color: #00ff00;
            box-shadow: 0 0 0 1px #00ff00;
        }

        /* Botón Personalizado: Negro a Verde */
        :host ::ng-deep .usina-btn-submit {
            background-color: #000000;
            border: none;
            color: #ffffff;
            transition: all 0.3s ease;
        }
        :host ::ng-deep .usina-btn-submit:hover:not(:disabled) {
            background-color: #00ff00 !important;
            color: #000000 !important;
        }
        :host ::ng-deep .usina-btn-submit:disabled {
            background-color: #cccccc;
        }
    `]
})
export class ServiceDetailComponent implements OnInit, OnDestroy {

    private contactService = inject(ContactService);
    private seoService     = inject(SeoService);
    private platformId     = inject(PLATFORM_ID);
    service: ServiceData | undefined;
    form: FormGroup;
    showForm: boolean = false;
    isSubmitting: boolean = false;
    showSuccessModal: boolean = false;

    
    loginUrl = environment.userPanelUrl;

    sedes = [
        { name: 'Chacarita (Castillo 1366)', id: 'Chacarita' },
        { name: 'San Isidro (Av. Libertador esq. Ituzaingó)', id: 'San Isidro' }
    ];

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private fb: FormBuilder,
        private messageService: MessageService
    ) {
        this.form = this.fb.group({
        sede: ['', Validators.required],
        name: ['', [Validators.required, Validators.minLength(3)]],
        email: ['', [Validators.required, Validators.pattern(emailRegex)]],
        phone: ['', [Validators.required, Validators.pattern(phoneRegex)]],
        message: ['', [Validators.required, Validators.minLength(10)]], // Mínimo 15 caracteres
        membershipId: [''] 
    });
    }

    ngOnInit() {
        this.route.params.subscribe(params => {
            const slug = params['id'];
            this.loadServiceData(slug);
        });
    }

    loadServiceData(slug: string) {
        const data = SERVICES_DB[slug];

        if (data) {
            this.service = data;

            // SEO: title, description, canonical, OG y Twitter Card específicos por servicio
            this.seoService.update({
                title:         `${data.title} - Usina Cowork | Coworking Buenos Aires`,
                description:   `${data.description} ${data.features.slice(0, 2).join('. ')}.`,
                canonicalPath: `/service/${slug}`,
                ogImage:       `https://www.usinaco.work/assets/img/home/${data.image}`,
                ogType:        'product'
            });

            // SEO: BreadcrumbList schema
            this.seoService.injectSchema({
                '@context': 'https://schema.org',
                '@type':    'BreadcrumbList',
                'itemListElement': [
                    { '@type': 'ListItem', 'position': 1, 'name': 'Inicio',    'item': 'https://www.usinaco.work/' },
                    { '@type': 'ListItem', 'position': 2, 'name': data.title,  'item': `https://www.usinaco.work/service/${slug}` }
                ]
            }, 'schema-breadcrumb');

            this.form.patchValue({ membershipId: data.id });
            this.showForm = false;
            // window no existe en SSR/pre-render
            if (isPlatformBrowser(this.platformId)) {
                window.scrollTo(0, 0);
            }
        } else {
            this.router.navigate(['/']);
        }
    }

    ngOnDestroy(): void {
        // Limpiar schemas al salir de la página de detalle
        this.seoService.removeSchema('schema-breadcrumb');
    }

    toggleForm() {
        this.showForm = !this.showForm;
    }

    navigate(id: string) {
        this.router.navigate(['/']).then(() => {
            setTimeout(() => {
                const element = document.querySelector(id);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 100);
        });
    }

    onSubmit() {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        this.isSubmitting = true;

        // Mapeamos al Payload Universal
        const formValue = this.form.value;
        const payload = {
            sede: formValue.sede,
            serviceType: formValue.membershipId, // Usamos membershipId como serviceType
            name: formValue.name,
            email: formValue.email,
            phone: formValue.phone,
            message: formValue.message
        };

        this.contactService.submit(payload).subscribe({
            next: () => {
                this.isSubmitting = false;
                this.showSuccessModal = true;

                this.form.reset();
                // Restauramos el id del servicio actual
                if (this.service) {
                    this.form.patchValue({ membershipId: this.service.id });
                }
            },
            error: (err) => {
                this.isSubmitting = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Hubo un problema al enviar tu consulta. Intenta nuevamente.'
                });
            }
        });
    }
}